import { RefObject, useEffect } from "react";

interface Props {
    tableWrapperRef: RefObject<HTMLDivElement | null>;
    setIsHeaderSticky: (value: boolean) => void;
    setHeaderWidth: (value: number) => void;
    setHeaderLeft: (value: number) => void;
    setStickyTop: (value: number) => void;
}

export default function useStickyTableHeader({ tableWrapperRef, setIsHeaderSticky, setHeaderWidth, setHeaderLeft, setStickyTop, }: Props) {
    
    /*
     * Find the vertical scrolling parent.
     *
     * In PolicyDataPageModal this is the:
     *
     * <div className="flex-1 overflow-y-auto ...">
     *
     * We use this element to determine when the TAT header
     * has reached the top of the modal.
     */
    useEffect(() => {
        const tableWrapper = tableWrapperRef.current;

        if (!tableWrapper) return;

        const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
            let parent = element?.parentElement ?? null;

            while (parent) {
                const style = window.getComputedStyle(parent);

                if (style.overflowY === "auto" || style.overflowY === "scroll" || style.overflowY === "overlay" ) {
                    return parent;
                }
                parent = parent.parentElement;
            }
            return null;
        };

        const scrollParent = findScrollParent(tableWrapper);
        if (!scrollParent) return;

        const updateStickyState = () => {
            const wrapperRect = tableWrapper.getBoundingClientRect();
            const scrollParentRect = scrollParent.getBoundingClientRect();

            const shouldStick = wrapperRect.top <= scrollParentRect.top && wrapperRect.bottom > scrollParentRect.top + 1;

            setIsHeaderSticky(shouldStick);
            setStickyTop(scrollParentRect.top);
            setHeaderLeft(wrapperRect.left);
            setHeaderWidth(wrapperRect.width);
        };

        updateStickyState();

        scrollParent.addEventListener("scroll", updateStickyState, {
            passive: true,
        });

        window.addEventListener("resize", updateStickyState);

        return () => {
            scrollParent.removeEventListener("scroll", updateStickyState);
            window.removeEventListener("resize", updateStickyState);
        };
    }, [ tableWrapperRef, setIsHeaderSticky, setHeaderWidth, setHeaderLeft, setStickyTop, ]);
}